import type { DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { DndContext, PointerSensor, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { faFileImport, faInfoCircle, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, useMantineTheme } from '@mantine/core';
import { Observer, observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer, ISegmentDefinition, MailrailBaseEmailCampaign } from '@feathr/blackbox';
import { getSegmentsUniqueEmailsPayload } from '@feathr/blackbox';
import {
  Button,
  CardActions,
  CardContent,
  CardHeader,
  CardV2,
  EmptyState,
  Fieldset,
  Text,
  toast,
  Tooltip,
  Well,
} from '@feathr/components';
import ConfirmButton from '@feathr/extender/components/ConfirmButton';
import type { ISegmentSelectSimpleChangeProps } from '@feathr/extender/components/SegmentSelectSimple';
import { useUniqueEmailsCount } from '@feathr/extender/hooks';
import { useLocalUrl, useStore, useUser } from '@feathr/extender/state';
import { moment, TimeFormat } from '@feathr/hooks';

import type { TSegmentContainerId } from './DraggableSegmentSelect';
import DraggableSegmentSelect from './DraggableSegmentSelect';

import * as styles from './MailrailCampaignSegments.css';

const MATCH_ALL_SEGMENT: ISegmentDefinition = {
  lookback_mode: 'unbounded',
  mode: 'match_all',
  predicates: [],
};

function getOverContainer(event: DragOverEvent): TSegmentContainerId | undefined {
  const overData = event.over?.data.current as
    | { containerId?: TSegmentContainerId; sortable?: { containerId: TSegmentContainerId } }
    | undefined;
  return overData?.sortable?.containerId ?? overData?.containerId;
}

interface IDroppableFieldsetProps {
  children: React.ReactNode;
  className?: string;
  containerId: TSegmentContainerId;
  label: string;
}

function DroppableFieldset({
  children,
  className,
  containerId,
  label,
}: Readonly<IDroppableFieldsetProps>): JSX.Element {
  const { setNodeRef } = useDroppable({ id: containerId, data: { containerId } });

  return (
    <Fieldset className={className} label={label}>
      <div ref={setNodeRef}>{children}</div>
    </Fieldset>
  );
}

interface IProps {
  campaign: MailrailBaseEmailCampaign;
  disabled: boolean;
  step?: number;
}

function MailrailCampaignSegments({ campaign, disabled, step }: Readonly<IProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const user = useUser();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { Goals, Importers, Targetings } = useStore();
  const importerRef = useRef<Importer | undefined>();
  const isTriggerable = campaign.isMailrailTriggerable;

  const uniqueEmailsCount = useUniqueEmailsCount(
    (Segments) => {
      const payload = getSegmentsUniqueEmailsPayload(Segments, campaign);
      if (payload.included_segment_ids?.length || payload.included_segments?.length) {
        return payload;
      }
      // Autosend and drip segments limit who is eligible: with no segments selected, the account's
      // entire audience is eligible to trigger the email or enroll in the drip.
      if (isTriggerable) {
        return { ...payload, included_segments: [MATCH_ALL_SEGMENT] };
      }
      // Single-send segments define the audience: with no inclusion segments selected, no one
      // receives the email. (At least one inclusion segment is required in order to publish.)
      return null;
    },
    [campaign],
  );
  // The count is unknown (undefined) until the first request resolves, and again after a failed
  // request; the em-dash is the shared empty-value convention.
  const countDisplay = uniqueEmailsCount?.toLocaleString() ?? '—';

  const committedIncludedIds = campaign.get('segments_included', []);
  const committedExcludedIds = campaign.get('segments_excluded', []);
  const allSegmentIds = [...committedIncludedIds, ...committedExcludedIds];

  // During drag, local state tracks visual positions without committing to the campaign model until drop
  const [localIncludedIds, setLocalIncludedIds] = useState<string[] | null>(null);
  const [localExcludedIds, setLocalExcludedIds] = useState<string[] | null>(null);

  const includedIdsToRender = localIncludedIds ?? committedIncludedIds;
  const excludedIdsToRender = localExcludedIds ?? committedExcludedIds;

  const buttonTooltip = allSegmentIds.includes('')
    ? 'Finish selecting a group before adding another'
    : undefined;

  useEffect(() => {
    if (committedIncludedIds.filter(Boolean).length === 0) {
      campaign.set({ target_address_type: 'primary' });
    }
  }, [committedIncludedIds.filter(Boolean).length]);

  function setIncludedSegmentIds(newIds: string[]): void {
    campaign.set({ segments_included: newIds });
    if (newIds.filter(Boolean).length) {
      return;
    }
    campaign.set({ consent: { has_consent: false } });
    // Post-campaign bulk sends require at least one inclusion segment,
    // otherwise they would send to the account's entire audience
    if (campaign.isMailrailAutoSend && campaign.get('bulk_send_at_end')) {
      campaign.set({ bulk_send_at_end: false });
    }
  }

  function setExcludedSegmentIds(newIds: string[]): void {
    campaign.set({ segments_excluded: newIds });
  }

  async function addImporter(): Promise<string | void> {
    importerRef.current = undefined;
    try {
      const importerAttrs: Record<string, string> = {
        created_by: user.id,
        kind: 'person',
        state: 'draft',
        // TODO: Translate Importer name.
        name: `Import ${moment().format(TimeFormat.isoDate)}`,
      };

      const newImporter = Importers.create(importerAttrs);
      const response = await Importers.add(newImporter, { validate: false });

      importerRef.current = response;
      return response.get('segment');
      // Campaign is saved by <ConfirmButton />.
    } catch (e) {
      toast(t('There was an error creating the import.'), { type: 'error' });
    }
  }

  async function addIncludedImporter(): Promise<void> {
    const newSegmentId = await addImporter();
    if (newSegmentId) {
      setIncludedSegmentIds([...committedIncludedIds, newSegmentId]);
    }
  }

  function getRedirect(): string | false {
    if (!importerRef.current) {
      return false;
    }
    return localUrl(importerRef.current.getItemUrl());
  }

  function changeSegment(oldId: string | undefined, id: string, excluded: boolean): void {
    const segmentIds = excluded ? committedExcludedIds : committedIncludedIds;
    const oldIdIndex = oldId === undefined ? -1 : segmentIds.indexOf(oldId);
    if (oldIdIndex === -1) {
      // This should not happen.
      return;
    }

    const newIds = [...segmentIds];
    newIds[oldIdIndex] = id;
    if (excluded) {
      setExcludedSegmentIds(newIds);
    } else {
      setIncludedSegmentIds(newIds);
    }
  }

  function handleChangeIncludedSegment({ oldId, id }: ISegmentSelectSimpleChangeProps): void {
    changeSegment(oldId, id, false);
  }

  function handleChangeExcludedSegment({ oldId, id }: ISegmentSelectSimpleChangeProps): void {
    changeSegment(oldId, id, true);
  }

  function handleRemoveIncludedSegment(id?: string): void {
    if (id) {
      setIncludedSegmentIds(committedIncludedIds.filter((s) => s !== id));
    } else {
      setIncludedSegmentIds(committedIncludedIds.filter((s) => s !== ''));
    }
  }

  function handleRemoveExcludedSegment(id?: string): void {
    if (id) {
      setExcludedSegmentIds(committedExcludedIds.filter((s) => s !== id));
    } else {
      setExcludedSegmentIds(committedExcludedIds.filter((s) => s !== ''));
    }
  }

  function addIncludedSegment(): void {
    setIncludedSegmentIds([...committedIncludedIds, '']);
  }

  function addExcludedSegment(): void {
    setExcludedSegmentIds([...committedExcludedIds, '']);
  }

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  function handleDragStart(event: DragStartEvent): void {
    setActiveDragId(event.active.id as string);
    setLocalIncludedIds([...committedIncludedIds]);
    setLocalExcludedIds([...committedExcludedIds]);
  }

  function handleDragOver(event: DragOverEvent): void {
    const { active, over } = event;
    if (!over || !localIncludedIds || !localExcludedIds) {
      return;
    }

    const activeId = active.id as string;
    const overId = over.id as string;
    const overContainer = getOverContainer(event);

    if (!overContainer) {
      return;
    }

    const activeContainer: TSegmentContainerId = localIncludedIds.includes(activeId)
      ? 'included'
      : 'excluded';

    if (activeContainer === overContainer) {
      // The segment is just being moved to a different position within the same container
      const ids = activeContainer === 'included' ? [...localIncludedIds] : [...localExcludedIds];
      const fromIndex = ids.indexOf(activeId);
      const toIndex = ids.indexOf(overId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return;
      }
      ids.splice(fromIndex, 1);
      ids.splice(toIndex, 0, activeId);
      if (activeContainer === 'included') {
        setLocalIncludedIds(ids);
      } else {
        setLocalExcludedIds(ids);
      }
    } else {
      // The segment is being moved from included to excluded or vice-versa
      const sourceIds =
        activeContainer === 'included' ? [...localIncludedIds] : [...localExcludedIds];
      const destIds = overContainer === 'included' ? [...localIncludedIds] : [...localExcludedIds];
      const sourceIndex = sourceIds.indexOf(activeId);

      if (sourceIndex === -1) {
        return;
      }

      sourceIds.splice(sourceIndex, 1);
      const destIndex = destIds.indexOf(overId);
      if (destIndex >= 0) {
        destIds.splice(destIndex, 0, activeId);
      } else {
        destIds.push(activeId);
      }

      if (activeContainer === 'included') {
        setLocalIncludedIds(sourceIds);
        setLocalExcludedIds(destIds);
      } else {
        setLocalExcludedIds(sourceIds);
        setLocalIncludedIds(destIds);
      }
    }
  }

  function handleDragEnd(): void {
    if (localIncludedIds && localExcludedIds) {
      setIncludedSegmentIds(localIncludedIds);
      setExcludedSegmentIds(localExcludedIds);
    }
    setActiveDragId(null);
    setLocalIncludedIds(null);
    setLocalExcludedIds(null);
  }

  function handleDragCancel(): void {
    setActiveDragId(null);
    setLocalIncludedIds(null);
    setLocalExcludedIds(null);
  }

  function getRecipientCountTooltip(): string {
    if (isTriggerable) {
      return t(
        'The number of unique email addresses among contacts who match the selected ' +
          'groups and are not opted out. This can fluctuate throughout the ' +
          "campaign's lifecycle as a result of incoming activities, contact data " +
          'updates, and/or group filter edits.',
      );
    }
    return t(
      'The number of unique email addresses among contacts who match the selected ' +
        'groups and are not opted out.',
    );
  }

  function getGroupsCardDescription(): string {
    if (campaign.isMailrailDrip) {
      return t(
        'By default, your entire audience is eligible to enroll in this drip. Selecting groups ' +
          'allows you to narrow that eligibility.',
      );
    }
    if (campaign.isMailrailAutoSend) {
      return t(
        'By default, your entire audience is eligible to trigger this email. Selecting groups ' +
          'allows you to narrow that eligibility.',
      );
    }
    return t(
      'Choose or create groups of people to receive this email. Anyone in an included ' +
        "group will receive it as long as they aren't in any excluded group.",
    );
  }

  return (
    <CardV2 width={'full'}>
      <CardHeader
        description={getGroupsCardDescription()}
        title={isTriggerable ? t('Filter Groups (Optional)') : t('Groups')}
      >
        <Tooltip title={getRecipientCountTooltip()}>
          <Well
            className={styles.recipientCount}
            layout={'vertical'}
            name={'unique_recipients_count'}
          >
            <Text.DefaultMedium mantineComponent={'p'} truncate={false}>
              <FontAwesomeIcon className={styles.tooltipIcon} icon={faInfoCircle} />
              {isTriggerable ? t('Eligible unique recipients:') : t('Total unique recipients:')}
            </Text.DefaultMedium>
            <Text.DefaultMedium mantineComponent={'p'} truncate={false}>
              {countDisplay}
            </Text.DefaultMedium>
          </Well>
        </Tooltip>
      </CardHeader>
      <CardContent addVerticalGap={true}>
        <DndContext
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDragStart={handleDragStart}
          sensors={sensors}
        >
          <SortableContext
            id={'included'}
            items={includedIdsToRender}
            strategy={verticalListSortingStrategy}
          >
            <DroppableFieldset
              className={styles.segment}
              containerId={'included'}
              label={t('Included Groups')}
            >
              {!includedIdsToRender.length && (
                <EmptyState label={t('No included groups')} theme={'neutral'} />
              )}
              {includedIdsToRender.map((segmentId) => (
                <Observer key={segmentId}>
                  {(): JSX.Element => {
                    const affectedTargetings = Targetings.list({
                      filters: {
                        _parent__ne: campaign.id,
                        _target_data: segmentId || undefined,
                      },
                      pagination: {
                        distinct: 'parent',
                      },
                    });
                    const affectedGoals = Goals.list({
                      filters: { segment: segmentId || undefined },
                    });
                    return (
                      <DraggableSegmentSelect
                        affectedCampaigns={affectedTargetings.pagination.distinct}
                        affectedGoals={affectedGoals.pagination.count}
                        containerId={'included'}
                        disabled={disabled}
                        excludeIds={allSegmentIds.filter(Boolean)}
                        id={segmentId}
                        isBeingDragged={activeDragId === segmentId}
                        onChange={handleChangeIncludedSegment}
                        onRemove={handleRemoveIncludedSegment}
                        value={segmentId}
                      />
                    );
                  }}
                </Observer>
              ))}
            </DroppableFieldset>
          </SortableContext>
          <SortableContext
            id={'excluded'}
            items={excludedIdsToRender}
            strategy={verticalListSortingStrategy}
          >
            <DroppableFieldset
              className={styles.segment}
              containerId={'excluded'}
              label={t('Excluded Groups')}
            >
              {!excludedIdsToRender.length && (
                <EmptyState label={t('No excluded groups')} theme={'neutral'} />
              )}
              {excludedIdsToRender.map((segmentId) => (
                <Observer key={segmentId}>
                  {(): JSX.Element => {
                    const affectedTargetings = Targetings.list({
                      filters: {
                        _parent__ne: campaign.id,
                        _target_data: segmentId || undefined,
                      },
                      pagination: {
                        distinct: 'parent',
                      },
                    });
                    const affectedGoals = Goals.list({
                      filters: { segment: segmentId || undefined },
                    });
                    return (
                      <DraggableSegmentSelect
                        affectedCampaigns={affectedTargetings.pagination.distinct}
                        affectedGoals={affectedGoals.pagination.count}
                        containerId={'excluded'}
                        disabled={disabled}
                        excludeIds={allSegmentIds.filter(Boolean)}
                        id={segmentId}
                        isBeingDragged={activeDragId === segmentId}
                        onChange={handleChangeExcludedSegment}
                        onRemove={handleRemoveExcludedSegment}
                        value={segmentId}
                      />
                    );
                  }}
                </Observer>
              ))}
            </DroppableFieldset>
          </SortableContext>
        </DndContext>
      </CardContent>
      <CardActions contentClassName={styles.cardActions}>
        <Flex gap={theme.spacing['2']}>
          <Button
            disabled={disabled || allSegmentIds.includes('')}
            name={'add_included_segment'}
            onClick={addIncludedSegment}
            prefix={<FontAwesomeIcon icon={faPlus} />}
            tooltip={buttonTooltip}
          >
            {t('Add included group')}
          </Button>
          <Button
            disabled={disabled || allSegmentIds.includes('')}
            name={'add_excluded_segment'}
            onClick={addExcludedSegment}
            prefix={<FontAwesomeIcon icon={faPlus} />}
            tooltip={buttonTooltip}
          >
            {t('Add excluded group')}
          </Button>
        </Flex>
        <ConfirmButton
          description={t(
            'Before you can import contacts, the changes to your campaign need to be saved.',
          )}
          disabled={disabled || allSegmentIds.includes('')}
          model={campaign}
          name={'import_contacts'}
          onConfirm={addIncludedImporter}
          prefix={<FontAwesomeIcon icon={faFileImport} />}
          redirect={getRedirect}
          step={step}
          tooltip={buttonTooltip}
        >
          {t('Import contacts')}
        </ConfirmButton>
      </CardActions>
    </CardV2>
  );
}

export default observer(MailrailCampaignSegments);
